/**
 * Norwegian Airspace Restriction Zones Data
 * Sources: Luftfartstilsynet (Norwegian Civil Aviation Authority), Avinor
 *
 * These zones represent areas where drone flight is restricted or prohibited:
 * - Airports (5km radius control zones)
 * - Military installations
 * - Nature reserves with flight restrictions
 * - Government buildings and sensitive areas
 */

export const ZONE_TYPES = {
	AIRPORT: { color: '#FF0000', fillOpacity: 0.25, label: 'Airport', group: 'airports' },
	AIRPORT_CTR: { color: '#FF4444', fillOpacity: 0.15, label: 'Airport CTR', group: 'airports' },
	MILITARY: { color: '#8B0000', fillOpacity: 0.3, label: 'Military', group: 'military' },
	HELIPORT: { color: '#FF6600', fillOpacity: 0.2, label: 'Heliport', group: 'heliports' },
	HOSPITAL: {
		color: '#0066CC',
		fillOpacity: 0.15,
		label: 'Hospital Helipad',
		group: 'heliports',
	},
	GOVERNMENT: { color: '#4B0082', fillOpacity: 0.25, label: 'Government', group: 'government' },
	PRISON: { color: '#800080', fillOpacity: 0.25, label: 'Prison', group: 'government' },
	NATURE_RESERVE: {
		color: '#228B22',
		fillOpacity: 0.15,
		label: 'Nature Reserve',
		group: 'nature',
	},
};

// Major Norwegian airports with their control zones
export const airports = [
	// Major International Airports
	{
		id: 'OSL',
		name: 'Oslo Gardermoen',
		type: 'AIRPORT',
		lat: 60.1939,
		lng: 11.1004,
		radius: 5000,
		maxAlt: 120,
		notes: "Norway's main international airport",
	},
	{
		id: 'BGO',
		name: 'Bergen Flesland',
		type: 'AIRPORT',
		lat: 60.2934,
		lng: 5.2181,
		radius: 5000,
		maxAlt: 120,
		notes: "Western Norway's main airport",
	},
	{
		id: 'TRD',
		name: 'Trondheim Værnes',
		type: 'AIRPORT',
		lat: 63.4578,
		lng: 10.924,
		radius: 5000,
		maxAlt: 120,
		notes: "Central Norway's main airport",
	},
	{
		id: 'SVG',
		name: 'Stavanger Sola',
		type: 'AIRPORT',
		lat: 58.8767,
		lng: 5.6378,
		radius: 5000,
		maxAlt: 120,
		notes: "Rogaland's main airport",
	},
	{
		id: 'TOS',
		name: 'Tromsø Langnes',
		type: 'AIRPORT',
		lat: 69.6833,
		lng: 18.9189,
		radius: 5000,
		maxAlt: 120,
		notes: "Northern Norway's main airport",
	},
	{
		id: 'BOO',
		name: 'Bodø',
		type: 'AIRPORT',
		lat: 67.2692,
		lng: 14.3653,
		radius: 5000,
		maxAlt: 120,
		notes: "Nordland's main airport",
	},

	// Regional Airports
	{
		id: 'AES',
		name: 'Ålesund Vigra',
		type: 'AIRPORT',
		lat: 62.5625,
		lng: 6.1197,
		radius: 4000,
		maxAlt: 120,
	},
	{
		id: 'KRS',
		name: 'Kristiansand Kjevik',
		type: 'AIRPORT',
		lat: 58.2042,
		lng: 8.0853,
		radius: 4000,
		maxAlt: 120,
	},
	{
		id: 'HAU',
		name: 'Haugesund Karmøy',
		type: 'AIRPORT',
		lat: 59.3453,
		lng: 5.2083,
		radius: 4000,
		maxAlt: 120,
	},
	{
		id: 'MOL',
		name: 'Molde Årø',
		type: 'AIRPORT',
		lat: 62.7447,
		lng: 7.2625,
		radius: 3500,
		maxAlt: 120,
	},
	{
		id: 'KSU',
		name: 'Kristiansund Kvernberget',
		type: 'AIRPORT',
		lat: 63.1118,
		lng: 7.8244,
		radius: 3500,
		maxAlt: 120,
	},
	{
		id: 'ALF',
		name: 'Alta',
		type: 'AIRPORT',
		lat: 69.9761,
		lng: 23.3717,
		radius: 4000,
		maxAlt: 120,
	},
	{
		id: 'KKN',
		name: 'Kirkenes Høybuktmoen',
		type: 'AIRPORT',
		lat: 69.7258,
		lng: 29.8913,
		radius: 4000,
		maxAlt: 120,
	},
	{
		id: 'EVE',
		name: 'Harstad/Narvik Evenes',
		type: 'AIRPORT',
		lat: 68.4913,
		lng: 16.6781,
		radius: 5000,
		maxAlt: 120,
		notes: 'Also military base',
	},
	{
		id: 'BNN',
		name: 'Brønnøysund Brønnøy',
		type: 'AIRPORT',
		lat: 65.4611,
		lng: 12.2175,
		radius: 3000,
		maxAlt: 120,
	},
	{
		id: 'SSJ',
		name: 'Sandnessjøen Stokka',
		type: 'AIRPORT',
		lat: 65.9569,
		lng: 12.4689,
		radius: 3000,
		maxAlt: 120,
	},
	{
		id: 'MQN',
		name: 'Mo i Rana Røssvoll',
		type: 'AIRPORT',
		lat: 66.3639,
		lng: 14.3014,
		radius: 3000,
		maxAlt: 120,
	},
	{
		id: 'LKL',
		name: 'Lakselv Banak',
		type: 'AIRPORT',
		lat: 70.0686,
		lng: 24.9733,
		radius: 4000,
		maxAlt: 120,
	},
	{
		id: 'HMR',
		name: 'Hamar Stafsberg',
		type: 'AIRPORT',
		lat: 60.8181,
		lng: 11.0681,
		radius: 3000,
		maxAlt: 120,
	},
	{
		id: 'RYG',
		name: 'Moss Rygge',
		type: 'AIRPORT',
		lat: 59.3789,
		lng: 10.7856,
		radius: 4000,
		maxAlt: 120,
		notes: 'Former military, now civilian',
	},
	{
		id: 'TRF',
		name: 'Sandefjord Torp',
		type: 'AIRPORT',
		lat: 59.1867,
		lng: 10.2586,
		radius: 4000,
		maxAlt: 120,
	},
	{
		id: 'FRO',
		name: 'Florø',
		type: 'AIRPORT',
		lat: 61.5836,
		lng: 5.0247,
		radius: 3000,
		maxAlt: 120,
	},
	{
		id: 'SOG',
		name: 'Sogndal Haukåsen',
		type: 'AIRPORT',
		lat: 61.1561,
		lng: 7.1378,
		radius: 3000,
		maxAlt: 120,
	},
	{
		id: 'SDN',
		name: 'Sandane Anda',
		type: 'AIRPORT',
		lat: 61.83,
		lng: 6.1058,
		radius: 3000,
		maxAlt: 120,
	},
	{
		id: 'HOV',
		name: 'Ørsta-Volda Hovden',
		type: 'AIRPORT',
		lat: 62.18,
		lng: 6.0742,
		radius: 3000,
		maxAlt: 120,
	},
	{
		id: 'RRS',
		name: 'Røros',
		type: 'AIRPORT',
		lat: 62.5783,
		lng: 11.3422,
		radius: 3000,
		maxAlt: 120,
	},
	{
		id: 'OSY',
		name: 'Namsos Høknesøra',
		type: 'AIRPORT',
		lat: 64.4722,
		lng: 11.5786,
		radius: 3000,
		maxAlt: 120,
	},
	{
		id: 'RVK',
		name: 'Rørvik Ryum',
		type: 'AIRPORT',
		lat: 64.8383,
		lng: 11.1461,
		radius: 3000,
		maxAlt: 120,
	},

	// Svalbard
	{
		id: 'LYR',
		name: 'Longyearbyen Svalbard',
		type: 'AIRPORT',
		lat: 78.2461,
		lng: 15.4656,
		radius: 5000,
		maxAlt: 120,
		notes: 'Arctic airport - special regulations',
	},

	// Small/STOL airports
	{
		id: 'FDE',
		name: 'Førde Bringeland',
		type: 'AIRPORT',
		lat: 61.3911,
		lng: 5.7569,
		radius: 2500,
		maxAlt: 120,
	},
	{
		id: 'VDS',
		name: 'Vadsø',
		type: 'AIRPORT',
		lat: 70.0653,
		lng: 29.8447,
		radius: 3000,
		maxAlt: 120,
	},
	{
		id: 'BJF',
		name: 'Båtsfjord',
		type: 'AIRPORT',
		lat: 70.6006,
		lng: 29.6914,
		radius: 2500,
		maxAlt: 120,
	},
	{
		id: 'BVG',
		name: 'Berlevåg',
		type: 'AIRPORT',
		lat: 70.8714,
		lng: 29.0342,
		radius: 2500,
		maxAlt: 120,
	},
	{
		id: 'HVG',
		name: 'Honningsvåg Valan',
		type: 'AIRPORT',
		lat: 71.0097,
		lng: 25.9836,
		radius: 2500,
		maxAlt: 120,
	},
	{
		id: 'MEH',
		name: 'Mehamn',
		type: 'AIRPORT',
		lat: 71.0297,
		lng: 27.8267,
		radius: 2500,
		maxAlt: 120,
	},
	{
		id: 'HFT',
		name: 'Hammerfest',
		type: 'AIRPORT',
		lat: 70.6797,
		lng: 23.6686,
		radius: 3000,
		maxAlt: 120,
	},
	{
		id: 'SOJ',
		name: 'Sørkjosen',
		type: 'AIRPORT',
		lat: 69.7867,
		lng: 20.9594,
		radius: 2500,
		maxAlt: 120,
	},
	{
		id: 'LKN',
		name: 'Leknes',
		type: 'AIRPORT',
		lat: 68.1525,
		lng: 13.6094,
		radius: 3000,
		maxAlt: 120,
		notes: 'Lofoten',
	},
	{
		id: 'SVJ',
		name: 'Svolvær Helle',
		type: 'AIRPORT',
		lat: 68.2433,
		lng: 14.6692,
		radius: 2500,
		maxAlt: 120,
		notes: 'Lofoten',
	},
	{
		id: 'SKN',
		name: 'Stokmarknes Skagen',
		type: 'AIRPORT',
		lat: 68.5789,
		lng: 15.0336,
		radius: 2500,
		maxAlt: 120,
		notes: 'Vesterålen',
	},
	{
		id: 'ANX',
		name: 'Andøya Andenes',
		type: 'AIRPORT',
		lat: 69.2925,
		lng: 16.1442,
		radius: 4000,
		maxAlt: 120,
		notes: 'Also military/space center',
	},
	{
		id: 'NVK',
		name: 'Narvik Framnes',
		type: 'AIRPORT',
		lat: 68.4369,
		lng: 17.3867,
		radius: 2500,
		maxAlt: 120,
	},
];

// Military installations and restricted areas
export const militaryZones = [
	// Air Force Bases
	{
		id: 'MIL_ORL',
		name: 'Ørland Main Air Station',
		type: 'MILITARY',
		lat: 63.6989,
		lng: 9.6044,
		radius: 6000,
		maxAlt: 150,
		notes: 'F-35 base - highly restricted',
	},
	{
		id: 'MIL_BOD',
		name: 'Bodø Air Station',
		type: 'MILITARY',
		lat: 67.2692,
		lng: 14.3653,
		radius: 5000,
		maxAlt: 150,
		notes: 'QRA base',
	},
	{
		id: 'MIL_EVE',
		name: 'Evenes Air Station',
		type: 'MILITARY',
		lat: 68.4913,
		lng: 16.6781,
		radius: 5000,
		maxAlt: 150,
		notes: 'P-8 Poseidon base',
	},
	{
		id: 'MIL_AND',
		name: 'Andøya Air Station',
		type: 'MILITARY',
		lat: 69.2925,
		lng: 16.1442,
		radius: 5000,
		maxAlt: 150,
		notes: 'Maritime patrol',
	},
	{
		id: 'MIL_RYG',
		name: 'Rygge Air Station',
		type: 'MILITARY',
		lat: 59.3789,
		lng: 10.7856,
		radius: 4000,
		maxAlt: 150,
		notes: 'Reserve base',
	},
	{
		id: 'MIL_GAR',
		name: 'Gardermoen Military',
		type: 'MILITARY',
		lat: 60.21,
		lng: 11.08,
		radius: 3000,
		maxAlt: 150,
		notes: 'Military section',
	},
	{
		id: 'MIL_BAN',
		name: 'Banak Air Station',
		type: 'MILITARY',
		lat: 70.0686,
		lng: 24.9733,
		radius: 4000,
		maxAlt: 150,
		notes: 'Northern defense',
	},

	// Army Bases and Training Areas
	{
		id: 'MIL_REN',
		name: 'Rena Military Camp',
		type: 'MILITARY',
		lat: 61.15,
		lng: 11.35,
		radius: 8000,
		maxAlt: 200,
		notes: 'Major training area',
	},
	{
		id: 'MIL_SES',
		name: 'Sessvollmoen Camp',
		type: 'MILITARY',
		lat: 60.15,
		lng: 11.05,
		radius: 3000,
		maxAlt: 150,
		notes: 'Army base',
	},
	{
		id: 'MIL_TER',
		name: 'Terningmoen Camp',
		type: 'MILITARY',
		lat: 60.8,
		lng: 11.08,
		radius: 2500,
		maxAlt: 150,
		notes: 'Army base',
	},
	{
		id: 'MIL_HEI',
		name: 'Heistadmoen Camp',
		type: 'MILITARY',
		lat: 59.45,
		lng: 9.85,
		radius: 2500,
		maxAlt: 150,
		notes: 'Army base',
	},
	{
		id: 'MIL_SKJ',
		name: 'Skjold Camp',
		type: 'MILITARY',
		lat: 69.25,
		lng: 18.0,
		radius: 4000,
		maxAlt: 150,
		notes: 'Northern army base',
	},
	{
		id: 'MIL_BAR',
		name: 'Bardufoss',
		type: 'MILITARY',
		lat: 69.0558,
		lng: 18.5403,
		radius: 5000,
		maxAlt: 150,
		notes: 'Army aviation',
	},
	{
		id: 'MIL_SET',
		name: 'Setermoen Camp',
		type: 'MILITARY',
		lat: 68.8667,
		lng: 18.35,
		radius: 3000,
		maxAlt: 150,
		notes: 'Brigade North',
	},

	// Naval Bases
	{
		id: 'MIL_HAA',
		name: 'Haakonsvern Naval Base',
		type: 'MILITARY',
		lat: 60.32,
		lng: 5.25,
		radius: 3000,
		maxAlt: 100,
		notes: 'Main naval base',
	},
	{
		id: 'MIL_RAM',
		name: 'Ramsund Naval Station',
		type: 'MILITARY',
		lat: 68.4,
		lng: 16.5,
		radius: 2500,
		maxAlt: 100,
		notes: 'Submarine base',
	},
	{
		id: 'MIL_OLA',
		name: 'Olavsvern (former)',
		type: 'MILITARY',
		lat: 69.6,
		lng: 18.95,
		radius: 2000,
		maxAlt: 100,
		notes: 'Former submarine base',
	},

	// Firing Ranges
	{
		id: 'MIL_HJE',
		name: 'Hjerkinn Firing Range',
		type: 'MILITARY',
		lat: 62.22,
		lng: 9.55,
		radius: 15000,
		maxAlt: 300,
		notes: 'Large firing range - active',
	},
	{
		id: 'MIL_BLO',
		name: 'Blåtind Firing Range',
		type: 'MILITARY',
		lat: 69.15,
		lng: 18.2,
		radius: 10000,
		maxAlt: 250,
		notes: 'Northern firing range',
	},
];

// Heliports (hospitals, offshore, etc.)
export const heliports = [
	// Hospital Heliports
	{
		id: 'HELI_OUS',
		name: 'Oslo University Hospital',
		type: 'HOSPITAL',
		lat: 59.94,
		lng: 10.72,
		radius: 500,
		maxAlt: 50,
		notes: 'Ullevål',
	},
	{
		id: 'HELI_RIK',
		name: 'Rikshospitalet',
		type: 'HOSPITAL',
		lat: 59.95,
		lng: 10.71,
		radius: 500,
		maxAlt: 50,
	},
	{
		id: 'HELI_HUS',
		name: 'Haukeland Hospital',
		type: 'HOSPITAL',
		lat: 60.38,
		lng: 5.36,
		radius: 500,
		maxAlt: 50,
		notes: 'Bergen',
	},
	{
		id: 'HELI_STH',
		name: 'St. Olavs Hospital',
		type: 'HOSPITAL',
		lat: 63.42,
		lng: 10.39,
		radius: 500,
		maxAlt: 50,
		notes: 'Trondheim',
	},
	{
		id: 'HELI_SUS',
		name: 'Stavanger University Hospital',
		type: 'HOSPITAL',
		lat: 58.96,
		lng: 5.73,
		radius: 500,
		maxAlt: 50,
	},
	{
		id: 'HELI_UNN',
		name: 'UNN Tromsø',
		type: 'HOSPITAL',
		lat: 69.68,
		lng: 18.97,
		radius: 500,
		maxAlt: 50,
	},
	{
		id: 'HELI_NOR',
		name: 'Nordlandssykehuset',
		type: 'HOSPITAL',
		lat: 67.28,
		lng: 14.4,
		radius: 500,
		maxAlt: 50,
		notes: 'Bodø',
	},
	{
		id: 'HELI_HAM',
		name: 'Hammerfest Hospital',
		type: 'HOSPITAL',
		lat: 70.66,
		lng: 23.68,
		radius: 500,
		maxAlt: 50,
	},
	{
		id: 'HELI_KIR',
		name: 'Kirkenes Hospital',
		type: 'HOSPITAL',
		lat: 69.73,
		lng: 30.05,
		radius: 500,
		maxAlt: 50,
	},

	// Air Ambulance Bases
	{
		id: 'HELI_LOS',
		name: 'Lørenskog Air Ambulance',
		type: 'HELIPORT',
		lat: 59.93,
		lng: 10.96,
		radius: 800,
		maxAlt: 80,
		notes: 'Main air ambulance base',
	},
	{
		id: 'HELI_ALT',
		name: 'Alta Air Ambulance',
		type: 'HELIPORT',
		lat: 69.97,
		lng: 23.36,
		radius: 800,
		maxAlt: 80,
	},
	{
		id: 'HELI_BRO',
		name: 'Brønnøysund Air Ambulance',
		type: 'HELIPORT',
		lat: 65.46,
		lng: 12.21,
		radius: 800,
		maxAlt: 80,
	},
	{
		id: 'HELI_ARE',
		name: 'Ålesund Air Ambulance',
		type: 'HELIPORT',
		lat: 62.47,
		lng: 6.15,
		radius: 800,
		maxAlt: 80,
	},
	{
		id: 'HELI_FOR',
		name: 'Førde Air Ambulance',
		type: 'HELIPORT',
		lat: 61.46,
		lng: 5.86,
		radius: 800,
		maxAlt: 80,
	},
	{
		id: 'HELI_DON',
		name: 'Dombås Air Ambulance',
		type: 'HELIPORT',
		lat: 62.07,
		lng: 9.12,
		radius: 800,
		maxAlt: 80,
	},
	{
		id: 'HELI_EVN',
		name: 'Evenes Air Ambulance',
		type: 'HELIPORT',
		lat: 68.49,
		lng: 16.68,
		radius: 800,
		maxAlt: 80,
	},
];

// Government and sensitive areas
export const governmentZones = [
	// Royal Palace and Government
	{
		id: 'GOV_PAL',
		name: 'Royal Palace Oslo',
		type: 'GOVERNMENT',
		lat: 59.917,
		lng: 10.7275,
		radius: 1000,
		maxAlt: 120,
		notes: 'No fly zone',
	},
	{
		id: 'GOV_STO',
		name: 'Stortinget (Parliament)',
		type: 'GOVERNMENT',
		lat: 59.9127,
		lng: 10.7414,
		radius: 800,
		maxAlt: 120,
		notes: 'No fly zone',
	},
	{
		id: 'GOV_REG',
		name: 'Regjeringskvartalet',
		type: 'GOVERNMENT',
		lat: 59.915,
		lng: 10.752,
		radius: 800,
		maxAlt: 120,
		notes: 'Government quarter',
	},
	{
		id: 'GOV_SKA',
		name: 'Skaugum Royal Estate',
		type: 'GOVERNMENT',
		lat: 59.89,
		lng: 10.48,
		radius: 1500,
		maxAlt: 120,
		notes: 'Crown Prince residence',
	},

	// Prisons
	{
		id: 'PRI_HAL',
		name: 'Halden Prison',
		type: 'PRISON',
		lat: 59.12,
		lng: 11.39,
		radius: 1000,
		maxAlt: 120,
	},
	{
		id: 'PRI_RIN',
		name: 'Ringerike Prison',
		type: 'PRISON',
		lat: 60.17,
		lng: 10.25,
		radius: 1000,
		maxAlt: 120,
	},
	{
		id: 'PRI_ULL',
		name: 'Ullersmo Prison',
		type: 'PRISON',
		lat: 60.05,
		lng: 11.15,
		radius: 1000,
		maxAlt: 120,
	},
	{
		id: 'PRI_ILA',
		name: 'Ila Prison',
		type: 'PRISON',
		lat: 59.95,
		lng: 10.45,
		radius: 1000,
		maxAlt: 120,
	},
	{
		id: 'PRI_BER',
		name: 'Bergen Prison',
		type: 'PRISON',
		lat: 60.39,
		lng: 5.32,
		radius: 800,
		maxAlt: 120,
	},
	{
		id: 'PRI_TRO',
		name: 'Tromsø Prison',
		type: 'PRISON',
		lat: 69.65,
		lng: 18.96,
		radius: 800,
		maxAlt: 120,
	},

	// Other sensitive
	{
		id: 'GOV_FOR',
		name: 'Fornebu (former airport)',
		type: 'GOVERNMENT',
		lat: 59.895,
		lng: 10.62,
		radius: 500,
		maxAlt: 50,
		notes: 'Events area',
	},
];

// Nature reserves with drone restrictions
export const natureReserves = [
	{
		id: 'NAT_RON',
		name: 'Rondane National Park',
		type: 'NATURE_RESERVE',
		lat: 61.95,
		lng: 9.75,
		radius: 20000,
		notes: 'Wild reindeer area',
	},
	{
		id: 'NAT_DOV',
		name: 'Dovrefjell National Park',
		type: 'NATURE_RESERVE',
		lat: 62.25,
		lng: 9.5,
		radius: 25000,
		notes: 'Musk ox area',
	},
	{
		id: 'NAT_JOT',
		name: 'Jotunheimen National Park',
		type: 'NATURE_RESERVE',
		lat: 61.55,
		lng: 8.3,
		radius: 30000,
		notes: 'Mountain area',
	},
	{
		id: 'NAT_HAR',
		name: 'Hardangervidda National Park',
		type: 'NATURE_RESERVE',
		lat: 60.1,
		lng: 7.5,
		radius: 40000,
		notes: "Europe's largest plateau",
	},
	{
		id: 'NAT_FEM',
		name: 'Femundsmarka National Park',
		type: 'NATURE_RESERVE',
		lat: 62.2,
		lng: 11.8,
		radius: 15000,
		notes: 'Wilderness area',
	},
	{
		id: 'NAT_SVA',
		name: 'Svalbard Protected Areas',
		type: 'NATURE_RESERVE',
		lat: 78.5,
		lng: 16.0,
		radius: 50000,
		notes: 'Arctic wildlife - strict rules',
	},
	{
		id: 'NAT_ROE',
		name: 'Røst Bird Reserve',
		type: 'NATURE_RESERVE',
		lat: 67.5,
		lng: 12.0,
		radius: 5000,
		notes: 'Seabird colony - seasonal',
	},
	{
		id: 'NAT_RUN',
		name: 'Runde Bird Island',
		type: 'NATURE_RESERVE',
		lat: 62.4,
		lng: 5.63,
		radius: 3000,
		notes: 'Seabird colony - seasonal',
	},
];

/**
 * Get all zones combined
 */
export const getAllZones = () => {
	return [...airports, ...militaryZones, ...heliports, ...governmentZones, ...natureReserves];
};

/**
 * Get zones by group
 */
export const getZonesByGroup = group => {
	return getAllZones().filter(zone => ZONE_TYPES[zone.type]?.group === group);
};

/**
 * Check if a point is within any restricted zone
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Array} - List of zones the point is within
 */
export const getRestrictionsAtPoint = (lat, lng) => {
	return getAllZones().filter(zone => {
		const distance = haversineDistance(lat, lng, zone.lat, zone.lng);
		return distance <= zone.radius;
	});
};

/**
 * Calculate distance between two points using Haversine formula
 */
const haversineDistance = (lat1, lon1, lat2, lon2) => {
	const R = 6371000; // Earth's radius in meters
	const dLat = toRad(lat2 - lat1);
	const dLon = toRad(lon2 - lon1);
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return R * c;
};

const toRad = deg => (deg * Math.PI) / 180;

export default {
	ZONE_TYPES,
	airports,
	militaryZones,
	heliports,
	governmentZones,
	natureReserves,
	getAllZones,
	getZonesByGroup,
	getRestrictionsAtPoint,
};
